import { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Loader } from '@gravity-ui/uikit';
import type { SessionStore } from '../lib/session/store.js';
import { SessionProvider, useSession, useSessionStore } from '../lib/session/context.js';
import { TenantsApiProvider } from '../lib/api/context.js';
import { createTenantsApi } from '../lib/api/tenants.js';
import { ManagersApiProvider } from '../lib/api/managers-context.js';
import { createManagersApi } from '../lib/api/managers.js';
import { FinanceApiProvider } from '../lib/api/finance-context.js';
import { createFinanceApi } from '../lib/api/finance.js';
import { LoginScreen } from '../features/auth/login-screen.js';
import { InstallApp } from '../ui/install-app.js';
import { PasswordResetScreen } from '../features/auth/password-reset-screen.js';
import { createAppRouter } from './router.js';

/**
 * Ссылка из письма сброса пароля разбирается до роутера: роутер поднимается
 * после входа, а по ссылке приходит тот, кто войти как раз не может. Секрет —
 * в пути, а не в параметре: параметры чаще попадают в журналы прокси.
 */
function resetToken(pathname: string): string | null {
  const match = /^\/password-reset\/(.+)$/.exec(pathname);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

export function App({ session }: { session: SessionStore }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const [token, setToken] = useState(() => resetToken(window.location.pathname));

  useEffect(() => {
    void session.restore();
  }, [session]);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider store={session}>
        <InstallApp />
        {token === null ? (
          <Screen />
        ) : (
          <PasswordResetScreen
            token={token}
            onDone={() => {
              window.history.replaceState(null, '', '/');
              setToken(null);
            }}
          />
        )}
      </SessionProvider>
    </QueryClientProvider>
  );
}

function Screen() {
  const session = useSession();

  switch (session.status) {
    case 'restoring':
      return (
        <main className="flex h-full items-center justify-center" aria-busy="true">
          <Loader size="m" />
          <span className="sr-only">Проверяем сессию…</span>
        </main>
      );

    case 'anonymous':
    case 'mfa_required':
      return <LoginScreen />;

    case 'ready':
      // Ключ на статусе, а не на identity объекта: перезаход другим
      // сотрудником обязан пересобрать дерево роутера с нуля, а не оставить
      // данные предыдущей сессии в памяти запросов TanStack Query.
      return <Workspace key={session.staff.id} />;
  }
}

function Workspace() {
  const store = useSessionStore();
  const [tenants] = useState(() => createTenantsApi(store.authorizedFetch));
  const [managers] = useState(() => createManagersApi(store.authorizedFetch));
  const [finance] = useState(() => createFinanceApi(store.authorizedFetch));
  const [router] = useState(() => createAppRouter());

  return (
    <TenantsApiProvider api={tenants}>
      <ManagersApiProvider api={managers}>
        <FinanceApiProvider api={finance}>
          <RouterProvider router={router} />
        </FinanceApiProvider>
      </ManagersApiProvider>
    </TenantsApiProvider>
  );
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}
